'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, FaqAccordion, PageSection } from '@/components/ui';
import { spring, viewportOnce } from '@/lib/motion';
import { useTranslation } from '@/hooks/useTranslation';
import {
  AlertMock,
  ModerateMock,
  EngageMock,
  TriggerMock,
  FilterMock,
  BranchMock,
} from '@/components/workflow/WorkflowMocks';
import s from '@/components/workflow/Workflow.module.css';

/* metadata in ./layout.js — this page is a client component for i18n + motion. */

const STATS = [
  { value: '80%', label: 'Of routine volume handled automatically' },
  { value: '15+', label: 'Actions: alert, hide, reply, route, tag' },
  { value: '<30s', label: 'From trigger to action' },
  { value: '24/7', label: 'Always-on across social & support' },
];

const GET = [
  {
    title: 'Alert the Right People',
    text: 'Fire a reputation or influencer alert to exactly the right team the moment it matters: on the platform, over email, or into Slack and Teams.',
    Mock: AlertMock,
  },
  {
    title: 'Moderate & Protect',
    text: 'Automatically hide or delete toxic, spam and abusive content on owned channels, keeping every brand surface clean without a human in the loop.',
    Mock: ModerateMock,
    alt: true,
  },
  {
    title: 'Engage & Resolve',
    text: 'Reply with ResponseGenie™, route, escalate, tag, set priority, acknowledge and push to your CRM. 15+ actions, fully automated, around the clock.',
    Mock: EngageMock,
  },
  {
    title: 'Triggers for Every Moment',
    text: 'A new mention or a listening spike, an influential voice, a new ticket or reply, an SLA breach, a form submission, a closure. The instant it happens across social and support, your workflow fires.',
    Mock: TriggerMock,
    alt: true,
  },
  {
    title: 'Filters That Understand Meaning',
    text: 'Qualify on ContextualPulse™ aspects, opinions and emotion, and SignalSense™ signals. Your rules react to what a conversation means, not just the words in it.',
    Mock: FilterMock,
  },
  {
    title: 'Sequence, Branch & Parallelise',
    text: 'Chain actions one after another, split into rule-based paths, or run them together. Build sophisticated playbooks with drag-and-drop. No code.',
    Mock: BranchMock,
    alt: true,
  },
];

const STEPS = [
  {
    tab: '01 · Triggers',
    kicker: 'Triggers',
    title: 'Start From Any Signal',
    body: 'Every workflow begins with a trigger. Listen across the open social web and your support desk, and act the instant something happens, not hours later in a report.',
    points: [
      'Fire on a new mention, a new ticket, or a reply on an existing ticket',
      'Catch an influential voice or a reputation risk as it surfaces',
      'Trigger on listening spikes, SLA breaches, form submissions and closures',
      'One brand, every channel: social, review sites, news, forums and support',
    ],
  },
  {
    tab: '02 · AI Filters',
    kicker: 'AI Filters',
    title: 'Filter by Meaning, Not Keywords',
    body: 'This is what sets Locobuzz workflows apart. Conditions are enriched by our AI engines, so a rule can distinguish a billing complaint in anger from a neutral product query, automatically.',
    points: [
      'Qualify on ContextualPulse™ aspects, aspect-groups, opinions and emotion',
      'Combine attributes with AND/OR logic for precise, meaning-aware rules',
      'Route on SignalSense™ signals: emerging risk, influence, intent',
      'Detect actionability so only conversations that need action move forward',
    ],
  },
  {
    tab: '03 · Orchestration',
    kicker: 'Orchestration',
    title: 'Sequence, Branch & Parallelise',
    body: 'Turn your best playbook into a machine. Steps run one after another, split into rule-based paths, or execute in parallel, so a high-risk complaint escalates while a routine one is auto-resolved.',
    points: [
      'Chain steps in sequence, with delays and conditional paths',
      'Run actions in parallel: hide, tag and route at the same time',
      'Split into rule-based branches on any enriched attribute',
      'Build and edit visually with drag-and-drop. No engineering needed',
    ],
  },
  {
    tab: '04 · Actions',
    kicker: 'Actions',
    title: '15+ Actions, Fully Automated',
    body: 'From the first alert to the final CRM sync, workflows take real action on your behalf, so your team only touches what genuinely needs a human.',
    points: [
      'Alert & notify on-platform, over email, and in Slack and Teams',
      'Change status: escalate, close, hold, awaiting, reopen',
      'Reply with ResponseGenie™ or a canned response',
      'Set category & priority, assign, acknowledge, and push to CRM',
    ],
  },
  {
    tab: '05 · Moderation',
    kicker: 'Moderation',
    title: 'Keep Every Channel Clean',
    body: 'Protect your brand surface automatically. When a comment crosses the line, workflows can hide or delete it on owned channels, before it ever spreads.',
    points: [
      'Auto-hide or delete toxic, spam and abusive mentions on owned channels',
      'Keep advertising and campaign comment sections brand-safe 24/7',
      'Trigger on ContextualPulse™ toxicity and abuse detection',
      'Log every moderation action for a full, auditable trail',
    ],
  },
];

const FAQS = [
  { q: 'What is Workflow Automation in Locobuzz?', a: 'A rule engine that watches your social and support channels and acts on its own. You define the trigger, the conditions and the actions once, and it runs continuously across every connected channel.' },
  { q: 'What can trigger a workflow?', a: 'A new mention, a listening spike, an influential voice, a new ticket or reply, an SLA breach, a form submission or a ticket closure. Anything that happens across social listening and support can start a flow.' },
  { q: 'How are workflow filters "AI-enriched"?', a: 'Conditions read the output of our AI engines rather than raw text. ContextualPulse™ supplies aspects, opinions and emotion, and SignalSense™ adds risk, influence and intent, so rules react to what a conversation means.' },
  { q: 'Can workflows run actions in parallel?', a: 'Yes. Steps can run one after another with delays, split into rule-based branches, or execute together, so you can hide, tag and route the same mention at once.' },
  { q: 'Can it automatically hide or delete unwanted comments?', a: 'On owned channels, yes. Toxic, spam and abusive comments can be hidden, deleted or muted the moment they are detected, and every action is logged for an auditable trail.' },
  { q: 'Do I need engineering resources to build workflows?', a: 'No. Workflows are built and edited visually with drag-and-drop triggers, filters and actions. No code and no engineering tickets.' },
];

function SectionHead({ pill, title, accent, text }) {
  const { t } = useTranslation();
  return (
    <motion.div
      className={s.head}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewportOnce}
      transition={spring}
    >
      <span className="lb-pill">{t(pill)}</span>
      <h2 className="lb-h2">
        {t(title)} <span className="serif-accent italic">{t(accent)}</span>
      </h2>
      <p>{t(text)}</p>
    </motion.div>
  );
}

export default function WorkflowAutomationPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  return (
    <div className={s.page}>
      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section className={s.hero} aria-label={t('Workflow Automation')}>
        <div className={s.heroBg} aria-hidden="true" />
        <div className={s.heroScrim} aria-hidden="true" />
        <div className={`container ${s.heroInner}`}>
          <div className={s.heroGrid}>
            <div>
              <span className={s.heroPill}>
                <span className={s.heroDot} aria-hidden="true" />
                {t('Workflow Automation')}
              </span>
              <motion.h1
                className={s.heroTitle}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...spring, delay: 0.05 }}
              >
                {t('Put Your Brand on')} <span className="serif-accent italic" style={{ fontWeight: 400 }}>{t('Autopilot')}</span>
              </motion.h1>
              <p className={s.heroCopy}>
                {t("Locobuzz Workflow Automation is your brand's always-on operator. The moment a mention, a spike, an influential voice or a complaint appears, it acts on its own:")}{' '}
                <strong>{t("alerting the right people, hiding what's toxic, replying, routing, escalating and syncing to CRM")}</strong>.
              </p>
              <p className={s.heroCopy}>
                {t('Build it once with drag-and-drop triggers, AI-enriched filters, and sequential or parallel paths. Then let it run across social listening and support, 24/7.')}
              </p>
              <div className={s.heroActions}>
                <BookDemoButton label={t('Book a Demo')} />
                <a href="#how-it-works" className={s.ghostBtn}>{t('Explore the Engine')}</a>
              </div>
            </div>
            <div aria-hidden="true" />
          </div>

          <div className={s.stats}>
            {STATS.map((x) => (
              <div key={x.value} className={s.stat}>
                <div className={s.statValue}>{x.value}</div>
                <div className={s.statLabel}>{t(x.label)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className={s.heroPad} aria-hidden="true" />
      </section>

      {/* ── WHAT YOU GET ───────────────────────────────────────────────── */}
      <section className={s.section}>
        <div className="container">
          <SectionHead
            pill="What You Get"
            title="One Engine to Alert, Moderate &"
            accent="Engage"
            text="Workflow Automation turns your best playbook into a machine that runs your whole brand presence, from the first signal to the final action, around the clock."
          />
          <div className={s.getGrid}>
            {GET.map(({ title, text, Mock, alt }, i) => (
              <motion.article
                key={title}
                className={`${s.getCard} ${alt ? s.getCardAlt : ''}`}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ ...spring, delay: (i % 3) * 0.08 }}
              >
                <h3 className={s.getTitle}>{t(title)}</h3>
                <p className={s.getText}>{t(text)}</p>
                <Mock />
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI AT THE CORE ─────────────────────────────────────────────── */}
      <section className={s.sectionTight}>
        <div className="container">
          <motion.div
            className={s.core}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOnce}
            transition={spring}
          >
            <div>
              <div className={s.coreEyebrow}>{t('Powered by AI at the Core™')}</div>
              <h2 className={s.coreTitle}>
                {t('Rules That React to')} <span className="serif-accent italic" style={{ fontWeight: 400 }}>{t('Meaning, Not Keywords')}</span>
              </h2>
              <p className={s.coreCopy}>
                {t('Every workflow filter can be enriched by our proprietary AI engines, so automation understands intent, emotion and risk, and acts the way your best agent would.')}
              </p>
            </div>
            <img
              className={s.coreImg}
              src="/workflow-automation/ai-core-engines.png"
              width="995"
              height="793"
              loading="lazy"
              alt={t('ContextualPulse, SignalSense, ResponseGenie and AgentIQ chips surrounding the Locobuzz AI core')}
            />
          </motion.div>
        </div>
      </section>

      {/* ── INSIDE THE ENGINE ──────────────────────────────────────────── */}
      <section id="how-it-works" className={`${s.section} ${s.sectionTint}`} style={{ scrollMarginTop: 80 }}>
        <div className="container">
          <SectionHead
            pill="Inside the Engine"
            title="How a Locobuzz Workflow"
            accent="Works"
            text="Five layers, one workflow. A signal comes in, AI qualifies it, the flow decides the path, and the right actions run automatically."
          />

          <div className={s.engineFrame}>
            <img
              className={s.engineImg}
              src="/workflow-automation/five-step-workflow.png"
              width="1952"
              height="635"
              loading="lazy"
              alt={t('Signals from social channels flow into the AI engine, split into conditional paths, drive automated actions, and end at brand-safety moderation')}
            />
          </div>

          <div className={s.tabs} role="tablist" aria-label={t('Workflow layers')}>
            {STEPS.map((x, i) => (
              <button
                key={x.tab}
                type="button"
                role="tab"
                id={`wf-tab-${i}`}
                aria-selected={step === i}
                aria-controls={`wf-panel-${i}`}
                className={`${s.tab} ${step === i ? s.tabOn : ''}`}
                onClick={() => setStep(i)}
              >
                {t(x.tab)}
              </button>
            ))}
          </div>

          {/* All panels share one grid cell so the section keeps the height of the
              tallest panel and nothing jumps when switching tabs. */}
          <div className={s.panels}>
            {STEPS.map((x, i) => (
              <div
                key={x.tab}
                id={`wf-panel-${i}`}
                role="tabpanel"
                aria-labelledby={`wf-tab-${i}`}
                aria-hidden={step !== i}
                className={`${s.panel} ${step === i ? s.panelOn : ''}`}
              >
                <div>
                  <div className={s.kicker}>{t(x.kicker)}</div>
                  <h3 className={s.panelTitle}>{t(x.title)}</h3>
                  <p className={s.panelBody}>{t(x.body)}</p>
                </div>
                <ul className={s.points}>
                  {x.points.map((p) => (
                    <li key={p} className={s.point}>
                      <span className={s.tick} aria-hidden="true">
                        <Check size={12} color="var(--brand)" strokeWidth={3} />
                      </span>
                      <span>{t(p)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      <PageSection id="faqs" bg="page" tight>
        <motion.div
          className={s.head}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={spring}
        >
          <h2 className="lb-h2">
            {t('Everything you need to')} <span className="serif-accent italic">{t('know')}</span>
          </h2>
        </motion.div>
        <FaqAccordion items={FAQS.map((f) => ({ q: t(f.q), a: t(f.a) }))} columns={2} name="workflow-automation-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
